import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  PostClientConfirmPhone,
  PostClientSendOtpSmsResponse,
} from '@lt/api/dist/methods/client/types';
import { formatPhone } from 'src/utils/stringUtils';
import { resetPhoneFormat } from 'src/components/AuthNew/utils';
import { useTranslation } from '@lt/localization';
import styles from './smsCodeForm.module.css';
import type { AuthConfig, GetNewCodeConfig, SetFormType } from '../../types';
import { AuthFormType, RequestStatus } from '../../types';

import { Title } from '../Title';
import { Description } from '../Description';
import { Content } from '../Content';
import { SmsCodeInput } from './SmsCodeInput';
import { ActionItem } from '../ActionItem';
import { SendCodeAgainTimer } from '../SendCodeAgainTimer';
import { AuthSuccess } from '../AuthSuccess';
import { otpErrors } from '../../errors';

interface Props {
  phone: string;
  requestStatus: RequestStatus;
  switchForm: SetFormType;
  onAuth?: (config: AuthConfig) => Promise<void>;
  onConfirmPhone?: (data: PostClientConfirmPhone) => Promise<void>;
  onGetNewCode: (
    config: GetNewCodeConfig,
  ) => Promise<PostClientSendOtpSmsResponse>;
  clientId?: number;
  timeLeftToDeliver: number;
  timer?: NodeJS.Timeout;
  isConfirmPhoneProcess: boolean;
  isAuthProcess: boolean;
}

const FIELDS_COUNT = 4;

export const SmsCodeForm: React.FC<Props> = ({
  switchForm,
  phone,
  onAuth,
  onConfirmPhone,
  requestStatus,
  onGetNewCode,
  clientId,
  timeLeftToDeliver,
  timer,
  isConfirmPhoneProcess,
  isAuthProcess,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const prevValue = useRef('');

  const handleGetNewCode = async () => {
    setError('');
    await onGetNewCode({
      phone: resetPhoneFormat(phone),
      event: isConfirmPhoneProcess && clientId ? 'sign_up' : 'sign_in',
      ...(clientId
        ? {
            client_id: clientId,
          }
        : {}),
    });
  };

  const handleSendOtpCode = useCallback(
    async (otp_code: string): Promise<void> => {
      try {
        if (isAuthProcess || (isConfirmPhoneProcess && isAuthProcess)) {
          if (!onAuth) {
            throw new Error(`onAuth is not define: ${onAuth}`);
          }
          await onAuth({ login: resetPhoneFormat(phone), password: otp_code });
          return;
        }

        if (
          (!isAuthProcess && isConfirmPhoneProcess) ||
          isConfirmPhoneProcess
        ) {
          if (!clientId) {
            throw new Error(`clientId is not define: ${clientId}`);
          }
          if (!onConfirmPhone) {
            throw new Error(`onConfirmPhone is not define: ${onConfirmPhone}`);
          }
          await onConfirmPhone({
            otp_code,
            client_id: clientId,
          });
        }
      } catch (e) {
        console.dir(e);
        setError((e as Error)?.message ?? '');
      }
    },
    [phone, clientId],
  );

  useEffect(() => {
    /**
     * помогает избежать цикла после неудачного запроса авторизации
     * и до автоматического сброса value
     * */

    if (prevValue.current === value) {
      return;
    }

    if (value.length === FIELDS_COUNT) {
      setError('');
      handleSendOtpCode(value);
      prevValue.current = value;
    }
  }, [handleSendOtpCode, phone, value]);

  const authSuccessComponent = useMemo(
    () =>
      isConfirmPhoneProcess ? (
        <AuthSuccess label={t('checkout.smsCodeForm.confirmPhone')} />
      ) : (
        <AuthSuccess />
      ),
    [isConfirmPhoneProcess],
  );

  return (
    <>
      <Title>{t('checkout.smsCodeForm.title')}</Title>
      <Description>
        {t('checkout.smsCodeForm.description')} <br /> {formatPhone(phone)}
      </Description>
      <Content>
        <SmsCodeInput
          fields={FIELDS_COUNT}
          name="code"
          type="number"
          inputMode="numeric"
          onChangeCustom={setValue}
          disabled={
            requestStatus === RequestStatus.Submitting ||
            error === otpErrors.otp_attempts_limit
          }
          error={error}
        />
        <div className={styles.actions}>
          {requestStatus !== RequestStatus.Submitting &&
            requestStatus !== RequestStatus.Succeeded && (
              <>
                <SendCodeAgainTimer
                  timeLeftToDeliver={timeLeftToDeliver}
                  onSendCode={handleGetNewCode}
                  timer={timer}
                />
                {isAuthProcess && (
                  <ActionItem
                    className={styles.actionItem}
                    onClick={() => switchForm(AuthFormType.Login)}
                  >
                    {t('checkout.smsCodeForm.login')}
                  </ActionItem>
                )}
              </>
            )}
          {requestStatus === RequestStatus.Submitting && (
            <div className={styles.checkCode}>
              <div className={styles.checkCodeLoader} />{' '}
              {t('checkout.smsCodeForm.checkCode')}...
            </div>
          )}
          {requestStatus === RequestStatus.Succeeded && authSuccessComponent}
        </div>
      </Content>
    </>
  );
};
